import React, { useEffect, useImperativeHandle, useState, useCallback } from "react";
import { Box, Card, Checkbox, FormControlLabel, Stack, Typography } from "@curves/ui-base";
import clsx from "clsx";
import { displayDateText } from "@util/moment";
import { isEmpty, isFunction } from "@util/utils";
import OrderCartList from "@icoach/accountBook/createOrder/components/OrderCartList";
import { convertBackendOrderItemsToCartItems } from "@icoach/accountBook/createOrder/CreateOrderPage.util";

/**
 * 賒帳訂單列表組件（未結清的過往訂單）
 * 注意：賒帳訂單不在折抵範圍內，不套用折扣資訊
 */
const UnpaidOrderList = React.forwardRef(({ orders, updateOrderTotal }, ref) => {
	const [selectedIds, setSelectedIds] = useState(() => new Set((orders || []).map(order => order.orderID)));

	// 當訂單列表變更時，同步選取狀態（預設全選）
	useEffect(() => {
		setSelectedIds(new Set((orders || []).map(order => order.orderID)));
		if (isFunction(updateOrderTotal)) {
			updateOrderTotal();
		}
		// eslint-disable-next-line
	}, [orders, updateOrderTotal]);

	// 計算選中訂單的總額資料
	const calculateTotalData = useCallback(
		() => {
			if (!Array.isArray(orders)) return { total: 0 };
			const total = orders.reduce((sum, order) => (selectedIds.has(order.orderID) ? sum + Number(order.payablePrice || 0) : sum), 0);
			return { total };
		},
		// eslint-disable-next-line
		[orders, selectedIds],
	);

	// 取得選中的訂單 ID 列表
	const getSelectedOrderIDsResult = useCallback(
		() => {
			return {
				checkoutOrderIDs: Array.from(selectedIds),
			};
		},
		// eslint-disable-next-line
		[selectedIds],
	);

	// 變更選取
	const handleToggle = useCallback(
		(orderID, checked) => {
			setSelectedIds(prev => {
				const next = new Set(prev);
				if (checked) {
					next.add(orderID);
				} else {
					next.delete(orderID);
				}
				return next;
			});
			if (isFunction(updateOrderTotal)) {
				updateOrderTotal();
			}
		},
		[updateOrderTotal],
	);

	// 當選取變更時通知父層更新總額
	useEffect(() => {
		if (isFunction(updateOrderTotal)) {
			updateOrderTotal();
		}
		// eslint-disable-next-line
	}, [selectedIds, updateOrderTotal]);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return getSelectedOrderIDsResult();
			},
			getTotalData: () => {
				return calculateTotalData();
			},
		}),
		// eslint-disable-next-line
		[orders, getSelectedOrderIDsResult, calculateTotalData],
	);

	return (
		<React.Fragment>
			<Typography className={"font-weight-medium mb-1"} variant={"h5"}>
				請選擇您要結帳的未付款訂單
			</Typography>
			{!isEmpty(orders) &&
				orders.map((order, index) => {
					const { orderID, orderNo, orderDate, payablePrice, items } = order;

					// 轉換後端的 items 為 OrderCartList 可使用的格式
					// 注意：賒帳訂單不在折抵範圍內，不套用折扣資訊
					const cartItems = convertBackendOrderItemsToCartItems(items);

					const isSelected = selectedIds.has(orderID);

					return (
						<Card key={orderID} className={clsx("order-cart unpaid-order", "mb-2", { "unpaid-order-selected": isSelected })}>
							<Card.CardContent>
								{/* 訂單標題和選擇框 */}
								<Stack className="unpaid-order-header" direction="row" alignItems="center">
									<FormControlLabel control={<Checkbox checked={selectedIds.has(orderID)} onChange={e => handleToggle(orderID, e.target.checked)} />} />
									<Typography component={"h4"} className={"unpaid-order-title"}>
										{`訂單編號：${orderNo}`}
										{orderDate && <span className={"font-color-3"}>{`於 ${displayDateText(orderDate)} 建立`}</span>}
									</Typography>
								</Stack>

								{/* 訂單內容：商品列表和金額資訊 */}
								<Box className={"order-cart-content"}>
									<OrderCartList cartItems={cartItems} cartTotal={payablePrice} readOnly showDiscount={false} />
								</Box>
							</Card.CardContent>
						</Card>
					);
				})}
		</React.Fragment>
	);
});

UnpaidOrderList.displayName = "UnpaidOrderList";

export default UnpaidOrderList;
